import getLocal from '@/app/utils/getLocal';
import { loginUser } from '@/utils/cookie';
import loginURL from '@/utils/loginURL';
import { getIsPadWidth } from '@elem/utils/devices';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

const langAppId = {
    us: 'ba99b897-1ccd-450f-871d-b98830ed6177',
    ca: 'ba99b897-1ccd-450f-871d-b98830ed6177',
};

const appIdMap = {
    us: langAppId.us,
    ca: langAppId.ca,
};

const regionCode = {
    us: 1,
    ca: 2,
    au: 3,
    za: 4,
    uk: 5,
    eu: 6,
    de: 7,
    fr: 12,
    cn: 13,
    jp: 14,
};

const langTxt: any = {
    us: `I'm online! Your Inteligent assistant is here ✨`,
    en: `I'm online! Your AI assistant is here ✨`,
    de: 'Ich bin online! Ihr KI-Assistent ist da ✨',
    jp: 'オンラインになりました！AIサポート登場 ✨',
    cn: '我上线啦！AI客服上线报道 ✨',
    fr: 'Je suis en ligne ! Votre assistant IA est là ✨',
};

const regionUrl: any = {
    us: 'https://m-9ae05e11-c6d8-ed11-aed0-000d3a64d144.eu.omnichannelengagementhub.com',
    ca: 'https://m-9ae05e11-c6d8-ed11-aed0-000d3a64d144.eu.omnichannelengagementhub.com',
};

// 采用新聊天窗窗样式的区域
const newChatBoxStyleSite = ['us', 'ca'];

const jpChatStyles = {
    confirmationPaneProps: {
        controlProps: {
            titleText: 'チャットを終了してもよろしいですか',
            hideSubtitle: true,
            confirmButtonText: 'はい',
            cancelButtonText: 'いいえ',
        },
        styleProps: {
            confirmButtonStyleProps: {
                fontSize: '12px',
            },
            cancelButtonStyleProps: {
                fontSize: '12px',
            },
        },
    },
};

function MicrosoftChatInDPUX({ payloadData }: any) {
    const [isLoaded, setIsLoaded] = useState(false);
    const isStartChat = useRef<boolean>(false);
    const [initState, setInitState] = useState<boolean>(false);

    const userInfo = loginUser();
    const { locale } = getLocal();
    const isMobile = getIsPadWidth(); //是否是移动端

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.lcw = () => {
            //会话前调查自适应卡片
            return {
                //注入payload
                preChatSurveyPaneProps: {
                    controlProps: {
                        payload: JSON.stringify(payloadData),
                    },
                },
                webChatContainerProps: {
                    renderingMiddlewareProps: {
                        systemMessageBoxStyles: {
                            maxWidth: '510px', //系统文本宽度（默认的padding 10px）
                        },
                        receivedMessageAnchorStyles: {
                            color: 'blue', //链接颜色
                        },
                    },
                    adaptiveCardStyles: {
                        background: 'white', //自适应卡片背景颜色
                        color: 'black', //自适应卡片字体颜色
                    },
                    webChatStyles: {
                        bubbleBackground: 'white', //系统背景颜色
                        bubbleTextColor: 'black', //系统字体颜色
                        bubbleFromUserBackground: '#94ed69', //用户输入文本背景颜色
                        bubbleFromUserTextColor: 'black', //用户输入字体颜色
                        bubbleMaxWidth: 500, //系统聊天文本最大宽度（默认250）
                    },
                },
                ...(locale === 'jp' ? jpChatStyles : {}),
            };
        };

        function contextProvider() {
            const data = {
                SiteUrl: { value: window.location.origin + (loginURL() || ''), isDisplayable: true },
                SiteName: { value: (regionCode as any)[locale || ''] || '', isDisplayable: true },
                Origin: { value: 'Official website', isDisplayable: true },
                ToAgent: {
                    value: 'true',
                    isDisplayable: true,
                },
            };
            //Here it is assumed that the corresponding work stream would have context variables with logical name of 'contextKey1', 'contextKey2', 'contextKey3'. If no context variable exists with a matching logical name, items are created assuming Type:string
            const res = Object.assign(
                data,
                userInfo && userInfo.user
                    ? {
                          Email: { value: userInfo.user.email, isDisplayable: true }, // boolean value
                      }
                    : {},
            );
            return res;
        }

        window.addEventListener('lcw:ready', function handleLivechatReadyEvent() {
            // Handle LiveChat Ready event
            // SDK methods are ready for use now
            // Setting custom context provider to be used with Chat for Dynamics 365
            // The custom context provided by custom context provider can be used for routing the chat to a particular queue
            (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.setContextProvider(contextProvider);
            // Starting a new chat
            //Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            setIsLoaded(true);
        });
        window.addEventListener('lcw:startChat', function handleLivechatStartedEvent() {
            // Handle the live chat started event
        });
        window.addEventListener('lcw:closeChat', function handleLivechatClosedEvent() {
            // Handle the live chat closed event
            setInitState(false);
        });
        window.addEventListener('lcw:onMessageReceived', function handleWidgetMessageReceivedEvent(payload) {
            isStartChat.current = true;
            setInitState(true);
            // Handle the live chat widget message sent event
        });
        window.addEventListener('lcw:onMinimize', function handleWidgetMinimizeEvent() {
            // Handle the live chat widget minimize event

            if (!isStartChat.current) {
                (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.closeChat();
            }
        });
    }, []);

    const open = () => {
        if (isStartChat) {
            (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            return;
        }
        if (!initState) {
            (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            setInitState(true);
        } else {
            (window as any).Microsoft.Omnichannel.LiveChatWidget.SDK.closeChat();
            setInitState(false);
        }
    };

    const appId = (appIdMap as any)[locale || ''];
    const orgUrl = regionUrl[locale] || regionUrl['other'];

    const errorFun = `(function(el){el.parentNode.removeChild(el);var s=document.createElement('script');s.src='https://ocprodpubliceurgs.blob.core.windows.net/livechatwidget/scripts/LiveChatBootstrapper.js';s.setAttribute('id', 'Microsoft_Omnichannel_LCWidget');s.setAttribute('data-app-id', ${appId});s.setAttribute('data-lcw-version', 'prod');s.setAttribute('data-org-id', '9ae05e11-c6d8-ed11-aed0-000d3a64d144');s.setAttribute('data-org-url', ${orgUrl});document.body.appendChild(s);})(this);`;

    return (
        <>
            <Helmet>
                {appId && (
                    <script
                        id="Microsoft_Omnichannel_LCWidget"
                        src="https://oc-cdn-public-eur.azureedge.net/livechatwidget/scripts/LiveChatBootstrapper.js"
                        onError={errorFun}
                        data-app-id={appId}
                        data-lcw-version="prod"
                        data-org-id="9ae05e11-c6d8-ed11-aed0-000d3a64d144"
                        data-org-url={orgUrl}
                        data-hide-chat-button="true"
                        defer
                        data-customization-callback={newChatBoxStyleSite.includes(locale) ? 'lcw' : ''}
                    />
                )}
            </Helmet>
        </>
    );
}

export default dynamic(() => Promise.resolve(MicrosoftChatInDPUX), {
    ssr: false,
});
